/*********************************************************************
* © Copyright IBM Corp. 2024
*********************************************************************/
import {useCallback} from 'react'
import _ from 'underscore'
import {
  ConfigurationAuditClient,
  ConfigurationAuditSearchOptions,
  ConfigurationChangeEventsFiltersParameters
} from '.'
import {AuditEvent, EnhancedAuditEvent} from '../ConfigurationList'
import {useAsyncList} from '../hooks/useAsyncList'
import {UserService} from './services/UserService'
import {ProcessService} from './services/ProcessService'
import {areaDependentComponents} from './areas'
import {TermService} from './services/TermService'
import intl from '../utils/intl/intl'

const convertOptions = (
  options: ConfigurationAuditSearchOptions
): ConfigurationChangeEventsFiltersParameters => {
  const convertedOptions: ConfigurationChangeEventsFiltersParameters = {...options}
  if (options.area) {
    convertedOptions.areas = [options.area]
  }
  if (options.eventType) {
    convertedOptions.eventTypes = [options.eventType]
  }

  return convertedOptions
}

const loadAuditEvents = async (
  client: ConfigurationAuditClient,
  options: ConfigurationAuditSearchOptions
): Promise<AuditEvent[]> => {
  try {
    const result = await client.getConfigurationChangeEvents(convertOptions(options))
    return result.items
  } catch (error) {
    return []
  }
}

const defaultOptions: ConfigurationAuditSearchOptions = {
  take: 50
}

async function getUserNames(userService: UserService, auditEvents: EnhancedAuditEvent[]) {
  const userIds = auditEvents.map((x) => x.userId)
  const userNames = await userService.getUserNames(userIds)

  auditEvents.forEach((x) => {
    x.userName = userNames.get(x.userId) || x.userId
  })
}

const defaultMessageBuilder = (item: EnhancedAuditEvent) =>
  intl.formatMessage(`Event of type {eventType} occurred.`, {eventType: item.eventType})

async function buildMessages(auditEvents: EnhancedAuditEvent[], termService: TermService) {
  const messagePromises = auditEvents.map((auditEvent) => {
    // nosemgrep: eslint.detect-object-injection
    const areaDependentSettings = areaDependentComponents[auditEvent.area]
    const messageBuilder = areaDependentSettings?.buildMessage ?? defaultMessageBuilder

    return messageBuilder(auditEvent, {termService})
  })
  const messages = await Promise.all(messagePromises)

  auditEvents.forEach((auditEvent, index) => {
    // nosemgrep: eslint.detect-object-injection
    auditEvent.message = messages[index]
  })
}

export const useConfigurationAuditList = ({
  client,
  userService,
  processService,
  initialOptions,
  termService
}: {
  client: ConfigurationAuditClient
  userService: UserService
  processService: ProcessService
  initialOptions?: ConfigurationAuditSearchOptions
  termService: TermService
}) => {
  const load = useCallback(
    async (searchOptions: ConfigurationAuditSearchOptions) => {
      const allAuditEvents = (await loadAuditEvents(client, searchOptions)) as EnhancedAuditEvent[]
      const auditEventsByArea = Object.entries(
        _.groupBy(allAuditEvents, (auditEvent) => auditEvent.area)
      )

      const areaSpecificDataPromises = auditEventsByArea
        .map(([area, auditEvents]) => {
          // nosemgrep: eslint.detect-object-injection
          return areaDependentComponents[area]?.populateData({
            processService,
            userService,
            auditEvents
          })
        })
        .filter((x) => x)

      await Promise.all([
        getUserNames(userService, allAuditEvents),
        ...areaSpecificDataPromises,
        buildMessages(allAuditEvents, termService)
      ])

      return allAuditEvents
    },
    [client]
  )
  const {items, options, setOptions, isLoading, hasMoreData, loadMoreData} = useAsyncList({
    defaultOptions: initialOptions ? {...defaultOptions, ...initialOptions} : defaultOptions,
    load
  })

  return {
    auditEvents: items,
    options,
    setOptions,
    isLoading,
    hasMoreData,
    loadMoreData,
    onUserIdChange: (userId?: string) => setOptions({...options, userId}),
    onQueryChange: (eventType?: string) =>
      setOptions({
        ...options,
        eventType
      }),
    onAreaChange: (area?: string) =>
      setOptions({
        ...options,
        area
      }),
    onClearFiltersClick: () =>
      setOptions({
        ...options,
        eventType: undefined,
        userId: undefined,
        area: undefined,
        createdAfter: undefined,
        createdBefore: undefined
      }),
    onDateChange: (dateFilter: {beforeDate?: Date; afterDate?: Date}) => {
      setOptions({
        ...options,
        createdBefore: dateFilter.beforeDate,
        createdAfter: dateFilter.afterDate
      })
    },
    hasNonEmptyFilter: (): boolean => {
      const {createdAfter, createdBefore, userId, area, eventType} = options
      const filters = [createdAfter, createdBefore, userId, area, eventType]
      return filters.some((filter) => Boolean(filter))
    }
  }
}
